import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Download, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PAYMENT_TYPES = ["Cash", "UPI", "Card", "Online"];

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

export default function SalesPage() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
const emptyForm = { cash_amount: "", upi_amount: "", card_amount: "", online_amount: "", notes: "", date: todayISO(),};
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const liveTotal =
  Number(form.cash_amount || 0) +
  Number(form.upi_amount || 0) +
  Number(form.card_amount || 0) +
  Number(form.online_amount || 0);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await api.get("/sales", { params });
      setSales(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      cash_amount: String(s.cash || ""),
      upi_amount: String(s.upi || ""),
      card_amount: String(s.card || ""),
      online_amount: String(s.online || ""),
      notes: s.notes || "",
      date: (s.date || "").slice(0, 10) || todayISO(),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const hasAmount =
      Number(form.cash_amount || 0) > 0 ||
      Number(form.upi_amount || 0) > 0 ||
      Number(form.card_amount || 0) > 0 ||
      Number(form.online_amount || 0) > 0;
    
    if (!hasAmount) {
      toast.error("Enter at least one sale amount");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        cash: Number(form.cash_amount || 0),
        upi: Number(form.upi_amount || 0),
        card: Number(form.card_amount || 0),
        online: Number(form.online_amount || 0),
        notes: form.notes,
        date: form.date,
      };
      if (editingId) {
        await api.patch(`/sales/${editingId}`, payload);
        toast.success("Sale updated");
      } else {
        await api.post("/sales", payload);
        toast.success("Sale added");
      }
      setForm(emptyForm);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save sale");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sale?")) return;
    try {
      await api.delete(`/sales/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.get("/sales/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `sales_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch (e) {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const clearDates = () => { setStartDate(""); setEndDate(""); };

  const canModify = (s) => user?.role === "admin" || s.created_by === user?.id;
  const total = sales.reduce((s, x) => s + x.amount, 0);

  return (
    <div className="p-6 sm:p-10 max-w-[1400px] mx-auto animate-fade-in-up" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-taupe-500 mb-2">Revenue log</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-espresso-900">
            Sales
          </h1>
          <p className="text-taupe-600 mt-2 text-sm">
            {sales.length} entries · <span className="font-semibold text-espresso-800">{formatINR(total)}</span> total
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={exportCsv}
            disabled={exporting || sales.length === 0}
            variant="outline"
            className="rounded-xl border-taupe-200 hover:border-espresso-800 hover:bg-taupe-100 text-espresso-800 font-semibold h-11"
            data-testid="export-sales-button"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>

          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button
                type="button"
                onClick={openAdd}
                className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold px-6 h-11"
                data-testid="add-sale-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Add sale
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-2xl h-[80vh] custom-scroll overflow-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight text-espresso-900">
                  {editingId ? "Edit sale" : "Add sale"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit}  className="space-y-4 " data-testid="sale-form">
                <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
  <p className="text-sm font-medium text-green-700">
    Total Sale Amount
  </p>

  <h2 className="mt-1 text-3xl font-bold text-green-800">
    {formatINR(liveTotal)}
  </h2>
</div>
<div className="space-y-3">
  <Label className="text-sm font-semibold text-espresso-800 block">
    Sales Amount
  </Label>

  <div className="grid grid-cols-1 gap-3">

    <div className="flex items-center gap-3">
      <Input
        value="Cash"
        disabled
        className="w-32 rounded-xl bg-taupe-100 border-taupe-200 font-semibold text-espresso-800"
      />
      <Input
        type="number"
        min="0"
        step="0.01"
        placeholder="0"
        value={form.cash_amount}
        onChange={(e) =>
          setForm({ ...form, cash_amount: e.target.value })
        }
        className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
      />
    </div>

    <div className="flex items-center gap-3">
      <Input
        value="UPI"
        disabled
        className="w-32 rounded-xl bg-taupe-100 border-taupe-200 font-semibold text-espresso-800"
      />
      <Input
        type="number"
        min="0"
        step="0.01"
        placeholder="0"
        value={form.upi_amount}
        onChange={(e) =>
          setForm({ ...form, upi_amount: e.target.value })
        }
        className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
      />
    </div>

    <div className="flex items-center gap-3">
      <Input
        value="Card"
        disabled
        className="w-32 rounded-xl bg-taupe-100 border-taupe-200 font-semibold text-espresso-800"
      />
      <Input
        type="number"
        min="0"
        step="0.01"
        placeholder="0"
        value={form.card_amount}
        onChange={(e) =>
          setForm({ ...form, card_amount: e.target.value })
        }
        className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
      />
    </div>

    <div className="flex items-center gap-3">
      <Input
        value="Online"
        disabled
        className="w-32 rounded-xl bg-taupe-100 border-taupe-200 font-semibold text-espresso-800"
      />
      <Input
        type="number"
        min="0"
        step="0.01"
        placeholder="0"
        value={form.online_amount}
        onChange={(e) =>
          setForm({ ...form, online_amount: e.target.value })
        }
        className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
      />
    </div>

  </div>
</div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                  data-testid="sale-date-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Description / notes</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g. Cappuccino + Sandwich"
                  className="rounded-xl bg-taupe-100 border-taupe-200"
                  data-testid="sale-notes-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold"
                  data-testid="submit-sale-button"
                >
                  {submitting ? "Saving…" : editingId ? "Update sale" : "Save sale"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Date range filter */}
      <div className="card-soft p-4 sm:p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="sales-filters">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-semibold text-taupe-600 mb-1.5 block uppercase tracking-wider">From</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
            data-testid="sales-start-date"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs font-semibold text-taupe-600 mb-1.5 block uppercase tracking-wider">To</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
            data-testid="sales-end-date"
          />
        </div>
        {(startDate || endDate) && (
          <Button
            type="button"
            variant="ghost"
            onClick={clearDates}
            className="rounded-xl h-11 text-taupe-600 hover:text-espresso-800"
            data-testid="clear-sales-filter"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="card-soft overflow-hidden" data-testid="sales-table-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-taupe-100 text-taupe-600 text-xs font-semibold uppercase tracking-wider">
                <th className="text-left py-4 px-6">Date</th>
                <th className="text-left py-4 px-6">Payment</th>
                <th className="text-left py-4 px-6">Notes</th>
                <th className="text-right py-4 px-6">Amount</th>
                <th className="text-right py-4 px-6" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="py-8 text-center text-taupe-500 text-sm">Loading…</td></tr>
              )}
              {!loading && sales.length === 0 && (
                <tr><td colSpan={5} className="py-12 text-center text-taupe-500 text-sm" data-testid="sales-empty">
                  No sales yet. Add your first entry.
                </td></tr>
              )}
              {sales.map((s) => (
                <tr key={s.id} className="border-t border-taupe-200 hover:bg-taupe-100/40 transition-colors" data-testid="sales-row">
                  <td className="py-4 px-6 text-sm text-espresso-900">{new Date(s.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
<td className="py-4 px-6 text-sm">
  <div className="flex flex-wrap gap-2">

    {Number(s.cash) > 0 && (
      <span className="inline-flex px-2 py-1 rounded-md bg-green-100 text-green-700 text-xs font-semibold">
        Cash ₹{s.cash}
      </span>
    )}

    {Number(s.upi) > 0 && (
      <span className="inline-flex px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-semibold">
        UPI ₹{s.upi}
      </span>
    )}

    {Number(s.card) > 0 && (
      <span className="inline-flex px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-semibold">
        Card ₹{s.card}
      </span>
    )}

    {Number(s.online) > 0 && (
      <span className="inline-flex px-2 py-1 rounded-md bg-orange-100 text-orange-700 text-xs font-semibold">
        Online ₹{s.online}
      </span>
    )}

  </div>
</td>
                  <td className="py-4 px-6 text-sm text-taupe-700 max-w-[300px] truncate">{s.notes || <span className="text-taupe-500">—</span>}</td>
                  <td className="py-4 px-6 text-right font-semibold text-espresso-900">{formatINR(s.amount)}</td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => openEdit(s)}
                        disabled={!canModify(s)}
                        className="p-2 rounded-lg text-espresso-800 hover:bg-taupe-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`edit-sale-${s.id}`}
                        aria-label="Edit"
                        title={canModify(s) ? "Edit" : "Only owner or admin can edit"}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(s.id)}
                        disabled={!canModify(s)}
                        className="p-2 rounded-lg text-terracotta-500 hover:bg-terracotta-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`delete-sale-${s.id}`}
                        aria-label="Delete"
                        title={canModify(s) ? "Delete" : "Only owner or admin can delete"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
